import React from 'react';
import { useTranslation } from 'react-i18next';
import { TbCalculator, TbBook, TbFileText, TbTrendingUp } from 'react-icons/tb';
import PageHeader from '../../components/misc/PageHeader';

export default function AccountingPage({
  hideHeader = false,
}: {
  hideHeader?: boolean;
}): JSX.Element {
  const { t } = useTranslation();

  return (
    <>
      {!hideHeader && (
        <PageHeader
          title="Accounting"
          subtitle="Official accounting administration with ledgers, journal posts and journal lines"
        />
      )}

      <div className="flex flex-col items-center justify-center py-16">
        <div className="text-center">
          <div className="mb-8 flex justify-center">
            <div className="rounded-full bg-primary-100 p-6">
              <TbCalculator className="text-primary-600 h-16 w-16" />
            </div>
          </div>

          <h2 className="mb-4 text-3xl font-bold text-gray-900">
            {t('page.financial.accounting.comingSoon.title', 'Accounting System Coming Soon')}
          </h2>

          <div className="mb-8 flex justify-center">
            <p className="max-w-2xl text-center text-lg text-gray-600">
              {t(
                'page.financial.accounting.comingSoon.subtitle',
                'We are working on implementing a comprehensive accounting system that follows official accounting rules with ledgers, journal posts, and journal lines.',
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <div className="mb-4 flex justify-center">
                <TbBook className="text-primary-600 h-8 w-8" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                {t('page.financial.accounting.features.ledgers.title', 'Ledgers')}
              </h3>
              <p className="text-sm text-gray-600">
                {t(
                  'page.financial.accounting.features.ledgers.description',
                  'Organize your accounts with proper ledger structure for accurate bookkeeping.',
                )}
              </p>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <div className="mb-4 flex justify-center">
                <TbFileText className="text-primary-600 h-8 w-8" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                {t('page.financial.accounting.features.journalPosts.title', 'Journal Posts')}
              </h3>
              <p className="text-sm text-gray-600">
                {t(
                  'page.financial.accounting.features.journalPosts.description',
                  'Create and manage journal entries with proper double-entry bookkeeping principles.',
                )}
              </p>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <div className="mb-4 flex justify-center">
                <TbTrendingUp className="text-primary-600 h-8 w-8" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                {t('page.financial.accounting.features.journalLines.title', 'Journal Lines')}
              </h3>
              <p className="text-sm text-gray-600">
                {t(
                  'page.financial.accounting.features.journalLines.description',
                  'Detailed line items for each journal entry with proper debit and credit handling.',
                )}
              </p>
            </div>
          </div>

          <div className="mt-8 rounded-lg bg-blue-50 p-6">
            <h4 className="mb-2 text-lg font-semibold text-primary">
              {t('page.financial.accounting.comingSoon.benefits.title', 'What to Expect')}
            </h4>
            <ul className="space-y-2 text-left text-primary">
              <li className="flex items-start">
                <span className="mr-2 text-primary-300">•</span>
                {t(
                  'page.financial.accounting.comingSoon.benefits.compliance',
                  'Full compliance with official accounting standards and regulations',
                )}
              </li>
              <li className="flex items-start">
                <span className="mr-2 text-primary-300">•</span>
                {t(
                  'page.financial.accounting.comingSoon.benefits.automation',
                  'Automated journal entry creation from your existing transactions',
                )}
              </li>
              <li className="flex items-start">
                <span className="mr-2 text-primary-300">•</span>
                {t(
                  'page.financial.accounting.comingSoon.benefits.reporting',
                  'Advanced financial reporting and analysis capabilities',
                )}
              </li>
              <li className="flex items-start">
                <span className="mr-2 text-primary-300">•</span>
                {t(
                  'page.financial.accounting.comingSoon.benefits.audit',
                  'Audit trail and complete transaction history for compliance',
                )}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}
